import React from 'react';
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import { Link } from 'react-router';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import { Statistic } from 'antd';
import { LoaderOverlay } from 'vdlib';
import BidComponent from '../bids/BidComponent';
import moment from 'moment';

const padDim = number => {
  const temp = '0' + number;
  return temp.substr(temp.length - 2);
};

const defaultBidObj = { Amount: 0, BidTime: null, BidderID: 0 };

const bidSorter = (
  { Amount: amount1, BidTime: bidTime1 },
  { Amount: amount2, BidTime: bidTime2 }
) => {
  if (amount1 > amount2) return -1;
  if (amount1 < amount2) return 1;
  const dt1 = new Date(bidTime1);
  const dt2 = new Date(bidTime2);
  return dt1 < dt2 ? -1 : dt1 > dt2 ? 1 : 0;
};

const onlineAuctionDuration = 15 * 60;

const winningStyle = { color: '#15770a' };
const losingStyle = { color: '#cf2d2f' };

const OnlineAuctionsDrawerItem = ({
  auction,
  loading,
  onBid,
  open,
  partyId,
  membershipID,
  merchantFees,
}) => {
  const [countdownReset, setCountdownReset] = React.useState(false);
  const initialRemainingTime = React.useMemo(
    () =>
      auction
        ? moment.duration(auction.OnlineEndDate.diff(moment.utc())).asSeconds()
        : null,
    [auction]
  );

  React.useEffect(() => {
    setCountdownReset(true);
    setTimeout(() => setCountdownReset(false));
  }, [initialRemainingTime]);

  // extract an object containing MY last bid and BEST bid
  const { best, my, winning } = React.useMemo(() => {
    if (
      partyId &&
      auction &&
      Array.isArray(auction.Bids) &&
      auction.Bids.length > 0
    ) {
      const best = auction.Bids.sort(bidSorter)[0] || defaultBidObj;
      const my =
        auction.Bids.find(b => b.BidderID === partyId) || defaultBidObj;
      return {
        best: best.Amount || 0,
        my: my.Amount || 0,
        winning: best.BidderID && best.BidderID === my.BidderID,
      };
    }
    return { best: 0, my: 0, winning: false };
  }, [auction, partyId]);

  // if outbidded, flash auction background
  const initialRef = React.useRef(false);
  const containerRef = React.useRef(null);
  const handleContainerRef = React.useCallback(elem => {
    containerRef.current = elem;
  }, []);
  React.useEffect(() => {
    if (!open) {
      initialRef.current = false;
    } else if (initialRef.current !== true) {
      initialRef.current = true;
    } else if (containerRef.current && !winning) {
      containerRef.current.classList.add('outbidded');
      setTimeout(() => containerRef.current.classList.remove('outbidded'));
    }
  }, [winning, open]);

  const { VehicleBrand, VehicleModel } = auction || {};

  const renderRemainingTime = React.useCallback(({ remainingTime }) => {
    const minutes = Math.floor(remainingTime / 60);
    const seconds = remainingTime - minutes * 60;

    return minutes === 0 ? (
      <div className="time-container">
        <div className="only-seconds-display">{padDim(seconds)}</div>
        <div>Δευτερόλεπτα</div>
      </div>
    ) : (
      <div className="time-container">
        <div className="time-display">
          {padDim(minutes)}:{padDim(seconds)}
        </div>
      </div>
    );
  }, []);

  return auction ? (
    <div
      ref={handleContainerRef}
      className={`online-auction ${winning ? 'winning' : 'losing'}`}
    >
      <LoaderOverlay loading={loading} />
      <div className="online-auction-top">
        <div>
          <Link to={`/auctions/${auction.ID}`}>
            <h4>{VehicleBrand}</h4>
            <h5>{VehicleModel}</h5>
          </Link>
          <Statistic
            className="amount-display"
            prefix="€"
            title="Καλύτερη Προσφορά:"
            value={best}
          />
          <Statistic
            className="amount-display"
            prefix="€"
            title="Η Προσφορά Μου:"
            value={my}
            valueStyle={winning ? winningStyle : losingStyle}
          />
        </div>
        <div className="countdown-container">
          {!countdownReset && initialRemainingTime >= 0 && (
            <CountdownCircleTimer
              colors={[
                ['#36c6d3', 0.5],
                ['#3598dc', 0.5],
              ]}
              duration={onlineAuctionDuration}
              initialRemainingTime={initialRemainingTime}
              isLinearGradient
              isPlaying
              rotation="counterclockwise"
              size={130}
              strokeWidth={10}
            >
              {renderRemainingTime}
            </CountdownCircleTimer>
          )}
        </div>
      </div>
      <BidComponent
        auction={auction}
        noBidsDisplay
        onSubmit={onBid}
        userId={partyId}
        merchantFees={merchantFees}
        membershipID={membershipID}
      />
    </div>
  ) : null;
};

OnlineAuctionsDrawerItem.propTypes = {
  auction: shape({
    Bids: arrayOf(
      shape({
        Amount: number,
        BidderID: number,
      })
    ),
    EndDate: object,
    VehicleBrand: string,
    VehicleModel: string,
  }),
  loading: bool,
  onBid: func,
  open: bool,
  partyId: number,
};

export default OnlineAuctionsDrawerItem;
